import React from "react";
import { FiEye, FiFilePlus, FiDownload, FiCheck, FiX } from "react-icons/fi";
import "../../../css/Views/TfgDashboardCard.css";

const STATUS = {
  NOTHING_TO_REPORT: 0,
  EXPORT_REQUIRED: 1,
  WAITING_FOR_EXPORT_RESULT: 2,
};

const EXPORT_TYPE_LABELS = {
  1: "NOWE DANE",
  2: "KOREKTA",
  3: "ROZWIĄZANIE",
  4: "USUNIĘCIE",
};

const TfgDashboardCard = ({
  dashboard,
  isRefreshing,
  isGenerating,
  isExportActionLoading,
  isDownloading,
  onRefresh,
  onOpenPreview,
  onGenerateExport,
  onDownloadExport,
  onAcceptExport,
  onRejectExport,
}) => {
  const exportTypeLabel =
    EXPORT_TYPE_LABELS[
      dashboard.nextExportType
    ] ?? "Nieznany typ";

  const checkedAtLabel =
    dashboard.checkedAt
      ? new Date(
          dashboard.checkedAt
        ).toLocaleString("pl-PL")
      : "Brak informacji";

  return (
    <section className="tfg-dashboard-card">
      <div className="tfg-dashboard-header">
        <div>
          <div className="tfg-dashboard-title">
            Raportowanie umów TFG
          </div>

          <div className="tfg-dashboard-date">
            Ostatnie sprawdzenie:{" "}
            {checkedAtLabel}
          </div>
        </div>

        <button
          type="button"
          className="tfg-refresh-button"
          onClick={onRefresh}
          disabled={
            isRefreshing ||
            isGenerating ||
            isExportActionLoading
          }
        >
          {isRefreshing
            ? "Odświeżanie..."
            : "Odśwież"}
        </button>
      </div>

{isGenerating || isExportActionLoading ? (
  <DashboardLoadingState
    isGenerating={isGenerating}
  />
) : (
  <>
    {dashboard.status ===
      STATUS.NOTHING_TO_REPORT && (
      <NothingToReportStatus />
    )}

    {dashboard.status ===
      STATUS.EXPORT_REQUIRED && (
      <ExportRequiredStatus
        exportTypeLabel={exportTypeLabel}
        itemsCount={dashboard.itemsCount}
        isGenerating={isGenerating}
        onOpenPreview={onOpenPreview}
        onGenerateExport={onGenerateExport}
      />
    )}

    {dashboard.status ===
      STATUS.WAITING_FOR_EXPORT_RESULT && (
      <WaitingForExportStatus
        openExport={dashboard.openExport}
        isLoading={isExportActionLoading}
        onDownload={onDownloadExport}
        onAccept={onAcceptExport}
        onReject={onRejectExport}
      />
    )}
  </>
)}
    </section>
  );
};

const NothingToReportStatus = () => (
  <div className="tfg-status-box tfg-status-box--success">
    <div className="tfg-status-title">
      Brak danych do przekazania
    </div>

    <div className="tfg-status-description">
      Wszystkie umowy są zgodne z ostatnio
      zaakceptowanymi eksportami.
    </div>
  </div>
);

const DashboardLoadingState = ({
  isGenerating,
}) => (
  <div className="tfg-status-box tfg-status-box--loading">
    <div className="tfg-loading-state">
      <div
        className="tfg-loading-spinner"
        aria-hidden="true"
      />

      <div>
        <div className="tfg-status-title">
          {isGenerating
            ? "Generowanie eksportu"
            : "Aktualizowanie statusu"}
        </div>

        <div className="tfg-status-description">
          {isGenerating
            ? "Trwa przygotowywanie pliku eksportu..."
            : "Trwa zapisywanie wyniku eksportu..."}
        </div>
      </div>
    </div>
  </div>
);

const ExportRequiredStatus = ({
  exportTypeLabel,
  itemsCount,
  isGenerating,
  onOpenPreview,
  onGenerateExport,
}) => {
  const contractsLabel =
    itemsCount === 1 ? " umowy." : " umów.";

  return (
    <div className="tfg-status-box tfg-status-box--warning">
      <div className="tfg-export-required-row">
        <div className="tfg-export-required-content">
          <div className="tfg-status-title">
            Wymagane jest przygotowanie eksportu{" "}
            <strong>{exportTypeLabel}</strong>{" "}
            dla{" "}
            <strong>
              {itemsCount} 
            </strong>
            {contractsLabel}
          </div>
        </div>

        <div className="tfg-dashboard-actions">
          <button
            type="button"
            className="tfg-button tfg-button--secondary"
            onClick={onOpenPreview}
            disabled={isGenerating}
          >
            <FiEye aria-hidden="true" />
            Podgląd
          </button>

          <button
            type="button"
            className="tfg-button tfg-button--primary"
            onClick={onGenerateExport}
            disabled={isGenerating}
          >
            <FiFilePlus aria-hidden="true" />

            {isGenerating
              ? "Generowanie..."
              : "Generuj"}
          </button>
        </div>
      </div>
    </div>
  );
};

const WaitingForExportStatus = ({
  openExport,
  isLoading,
  isDownloading,
  onDownload,
  onAccept,
  onReject,
}) => {
  if (!openExport) {
    return (
      <div className="tfg-status-box tfg-status-box--info">
        <div className="tfg-status-title">
          Eksport oczekuje na wczytanie
        </div>

        <div className="tfg-status-description">
          Nie udało się pobrać informacji o
          wygenerowanym eksporcie. Odśwież widok.
        </div>
      </div>
    );
  }

  const exportId =
    openExport.id ?? openExport.exportId;

  const fileName = openExport.fileName;

  return (
    <div className="tfg-status-box tfg-status-box--info">
      <div className="tfg-status-title">
        Eksport został wygenerowany
      </div>

      <div className="tfg-status-description">
        Pobierz plik, wczytaj go do systemu UFG,
        a następnie oznacz wynik operacji.
      </div>

      <div className="tfg-waiting-export-row">

        <button
          type="button"
          className="tfg-export-file"
          onClick={() =>
            onDownload(exportId, fileName)
          }
          disabled={isLoading || isDownloading || !exportId}
        >
            <span className="tfg-export-file-icon">
              {isDownloading ? (
                <span
                  className="tfg-mini-spinner"
                  aria-label="Pobieranie pliku"
                />
              ) : (
                <FiDownload aria-hidden="true" />
              )}
            </span>

            <span className="tfg-file-link">
              {fileName}
            </span>
        </button>

        <div className="tfg-dashboard-actions">
          <button
            type="button"
            className="tfg-button tfg-button--danger"
            onClick={() =>
              onReject(exportId, fileName)
            }
            disabled={isLoading || !exportId}
          >
            <FiX />
            Odrzucony
          </button>

          <button
            type="button"
            className="tfg-button tfg-button--success"
            onClick={() =>
              onAccept(exportId, fileName)
            }
            disabled={isLoading || !exportId}
          >
            <FiCheck />
            Wczytany
          </button>
        </div>

      </div>
    </div>
  );
};

export default TfgDashboardCard;